import React from 'react';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { companyInfo } from '../utils/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-b from-[#0D2436] to-[#061830] text-[#F5E6D3] pt-16 pb-8 border-t-2 border-[#E6A05C]/20">
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src="/vitanza-logo.png" 
                alt="Vitanza Logistics" 
                className="h-12 w-12 object-contain"
              />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-[#F5E6D3]">{companyInfo.name}</span>
                <span className="text-[#E6A05C] text-xs font-semibold tracking-widest">EST. {companyInfo.established}</span>
              </div>
            </div>
            <p className="text-[#F5E6D3]/70 mb-6 leading-relaxed">
              Your trusted partner in transportation excellence. Delivering reliability and safety across North America since {companyInfo.established}.
            </p>
            {/* Social Links */}
            <div className="flex space-x-4">
              <a href="#" className="w-10 h-10 bg-[#F5E6D3]/10 rounded-lg flex items-center justify-center hover:bg-[#E6A05C] transition-all duration-300 hover:scale-110">
                <Facebook size={20} />
              </a>
              <a href="#" className="w-10 h-10 bg-[#F5E6D3]/10 rounded-lg flex items-center justify-center hover:bg-[#E6A05C] transition-all duration-300 hover:scale-110">
                <Twitter size={20} />
              </a>
              <a href="#" className="w-10 h-10 bg-[#F5E6D3]/10 rounded-lg flex items-center justify-center hover:bg-[#E6A05C] transition-all duration-300 hover:scale-110">
                <Linkedin size={20} />
              </a>
              <a href="#" className="w-10 h-10 bg-[#F5E6D3]/10 rounded-lg flex items-center justify-center hover:bg-[#E6A05C] transition-all duration-300 hover:scale-110">
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <button onClick={() => scrollToSection('hero')} className="text-gray-400 hover:text-[#E6A05C] transition-colors duration-200">
                  Home
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-[#E6A05C] transition-colors duration-200">
                  Services
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-[#E6A05C] transition-colors duration-200">
                  About Us
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-[#E6A05C] transition-colors duration-200">
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6">Our Services</h3>
            <ul className="space-y-3">
              <li className="text-gray-400">Freight & Long Haul</li>
              <li className="text-gray-400">Logistics & Warehousing</li>
              <li className="text-gray-400">Cross Border US-Canada</li>
              <li className="text-gray-400">FTL Services</li>
              <li className="text-gray-400">LTL Services</li>
              <li className="text-gray-400">Reefer Services</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a href={`mailto:${companyInfo.email}`} className="flex items-start space-x-3 text-gray-400 hover:text-[#E6A05C] transition-colors duration-200 group">
                  <Mail className="mt-1 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" size={20} />
                  <span className="break-all">{companyInfo.email}</span>
                </a>
              </li>
              <li>
                <a href={`tel:${companyInfo.phoneTel}`} className="flex items-start space-x-3 text-gray-400 hover:text-[#E6A05C] transition-colors duration-200 group">
                  <Phone className="mt-1 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" size={20} />
                  <span>{companyInfo.phone}</span>
                </a>
              </li>
              <li className="flex items-start space-x-3 text-gray-400">
                <MapPin className="mt-1 flex-shrink-0" size={20} />
                <span>{companyInfo.address}</span>
              </li>
            </ul>

            {/* 24/7 Badge */}
            <div className="mt-6 bg-[#E6A05C]/20 border border-[#E6A05C]/50 rounded-lg p-4">
              <p className="text-[#E6A05C] font-bold">Available 24/7</p>
              <p className="text-sm text-gray-300">Emergency dispatch & support</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-[#E6A05C] transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-[#E6A05C] transition-colors duration-200">
                Terms of Service
              </a>
              <a href="#" className="text-gray-400 hover:text-[#E6A05C] transition-colors duration-200">
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
